import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { Order } from '@/types/order';
import { ApiRequestError, showApiResultModal } from '@/api';
import {
  RECURRING_INTERVALS,
  RECURRING_WEEKDAYS,
  joinRecurringServiceTime,
  postCreateRecurringRule,
  splitRecurringServiceTime,
} from '@/api/modules/recurringOrders';
import { listOrderAssignedStaff } from '@/lib/settleAllocation';
import { FormCheckbox } from '@/components/ui/FormCheckbox';
import { OptionSelect } from '@/components/ui/OptionSelect';
import { RecurringServiceTimeFields } from '@/pages/orders/components/RecurringServiceTimeFields';

interface CreateRecurringOrderModalProps {
  order: Order | null;
  onClose: () => void;
}

function clockFromServiceTime(serviceTime: string): string {
  const { meridiem, clock } = splitRecurringServiceTime(serviceTime);
  return joinRecurringServiceTime(clock, meridiem);
}

function weekdayFromServiceTime(serviceTime: string): number {
  const parsed = dayjs(serviceTime);
  if (!parsed.isValid()) return 0;
  const jsDay = parsed.day();
  return jsDay === 0 ? 6 : jsDay - 1;
}

export default function CreateRecurringOrderModal({
  order,
  onClose,
}: CreateRecurringOrderModalProps) {
  const staff = useMemo(() => (order ? listOrderAssignedStaff(order) : []), [order]);
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [serviceTime, setServiceTime] = useState('09:00');
  const [totalTimes, setTotalTimes] = useState('4');
  const [operatorIds, setOperatorIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!order) return;
    setIntervalWeeks(1);
    setDayOfWeek(weekdayFromServiceTime(order.service_time));
    setServiceTime(clockFromServiceTime(order.service_time));
    setTotalTimes('4');
    setOperatorIds(staff.map((person) => person.operatorId));
    setError('');
    setSubmitting(false);
  }, [order, staff]);

  if (!order) return null;

  const toggleOperator = (operatorId: string) => {
    setOperatorIds((current) => (
      current.includes(operatorId)
        ? current.filter((id) => id !== operatorId)
        : [...current, operatorId]
    ));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const visits = Number(totalTimes);
    if (!Number.isInteger(visits) || visits < 1) {
      setError('Enter the total number of visits.');
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(serviceTime)) {
      setError('Choose a service time.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await postCreateRecurringRule({
        orderId: order.id,
        intervalWeeks,
        dayOfWeek,
        serviceTime,
        totalTimes: visits,
        operatorIds,
      });
      showApiResultModal({ message: result.message, success: true });
      onClose();
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Failed to create the recurring rule.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
      <div className="relative w-full max-w-[430px] bg-white rounded-t-2xl px-4 pt-4 pb-6 shadow-xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-bold text-gray-900">Create Recurring</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400" aria-label="Close">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Repeat order #{order.id} on a schedule. You can edit or stop it later from Recurring Orders.
        </p>

        <label className="block text-xs font-semibold text-gray-600 mb-1">Repeat</label>
        <OptionSelect
          className="mb-3"
          value={String(intervalWeeks)}
          options={RECURRING_INTERVALS.map((item) => ({ value: String(item.weeks), label: item.label }))}
          onChange={(value) => setIntervalWeeks(Number(value))}
        />

        <label className="block text-xs font-semibold text-gray-600 mb-1">Weekday</label>
        <OptionSelect
          className="mb-3"
          value={String(dayOfWeek)}
          options={RECURRING_WEEKDAYS.map((label, index) => ({ value: String(index), label }))}
          onChange={(value) => setDayOfWeek(Number(value))}
        />

        <label className="block text-xs font-semibold text-gray-600 mb-1">Service time</label>
        <RecurringServiceTimeFields
          value={serviceTime}
          onChange={setServiceTime}
        />

        <label className="block text-xs font-semibold text-gray-600 mb-1">Total visits</label>
        <input
          type="number"
          min={1}
          step={1}
          value={totalTimes}
          onChange={(e) => setTotalTimes(e.target.value)}
          className="w-full h-10 mb-3 rounded-lg border border-gray-200 px-3 text-sm text-gray-800"
        />

        {staff.length > 0 ? (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-600 mb-1">Default staff</div>
            <p className="text-[10px] text-gray-400 leading-relaxed mb-2">
              Each new visit starts with these staff. You can reassign a visit later if someone is away. Replaced staff see that visit as cancelled, and the new staff see it as a new order.
            </p>
            <div className="space-y-1.5">
              {staff.map((person) => (
                <FormCheckbox
                  key={person.operatorId}
                  checked={operatorIds.includes(person.operatorId)}
                  label={person.name}
                  onChange={() => toggleOperator(person.operatorId)}
                />
              ))}
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
        ) : null}

        <button
          type="button"
          disabled={submitting}
          onClick={() => void handleSubmit()}
          className="w-full h-11 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Create Recurring'}
        </button>
      </div>
    </div>
  );
}
